/**
 * Content Slot Constants
 *
 * Single source of truth for content slot reserved keys and multi-pick strategies.
 * Used by ContentSlotHelper for validation and reference resolution.
 *
 * @module v2-variable-utils
 */

// ============================================================================
// RESERVED SLOT KEYS
// ============================================================================

/**
 * Slot key names reserved by the @variable.slotKey reference syntax.
 * These cannot be used as content dictionary keys in VariableOption.content.
 *
 * - `value` — resolves to VariableOption.id (the stable identifier)
 * - `label` — resolves to VariableOption.label
 * - `first` — multi-pick strategy: first selected option
 * - `last`  — multi-pick strategy: last selected option
 * - `all`   — multi-pick strategy: all selected options (comma-separated)
 */
export const RESERVED_SLOT_KEYS = [
  'value',
  'label',
  'first',
  'last',
  'all',
] as const;

export type ReservedSlotKey = typeof RESERVED_SLOT_KEYS[number];

// ============================================================================
// MULTI-PICK STRATEGIES
// ============================================================================

/**
 * Valid strategies for resolving content from enum[] variables.
 * Used as the third segment in @variableId.slotKey.strategy syntax.
 */
export const MULTI_PICK_STRATEGIES = ['first', 'last', 'all'] as const;

export type MultiPickStrategy = typeof MULTI_PICK_STRATEGIES[number];

/**
 * Default strategy when @variableId.slotKey is used on an enum[] variable
 * without an explicit strategy suffix.
 */
export const DEFAULT_MULTI_PICK_STRATEGY: MultiPickStrategy = 'all';

// ============================================================================
// VARIABLE TYPES THAT SUPPORT CONTENT SLOTS
// ============================================================================

/**
 * Only enum and enum[] variables support content dictionaries.
 * Other types (string, number, boolean, date) have no options → no content slots.
 */
export const CONTENT_SLOT_VARIABLE_TYPES = ['enum', 'enum[]'] as const;

/**
 * Content slot field types — exactly ONE should be set per slot.
 */
export const CONTENT_SLOT_FIELDS = ['text', 'image', 'clip', 'url'] as const;

export type ContentSlotField = typeof CONTENT_SLOT_FIELDS[number];
